<template>
  <div class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 sm:p-5 flex flex-col gap-4 text-slate-700">
    <div class="flex flex-wrap items-center justify-between gap-3 pb-2 border-b border-slate-200/60">
      <h3 class="text-xs font-bold uppercase tracking-wider text-slate-800 m-0">
        Avg Cashout per Day
      </h3>

      <div class="flex items-center gap-3">
        <button
          @click="toggleFilter"
          class="px-3 py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200/80 font-semibold rounded-xl text-xs transition-all shadow-2xs cursor-pointer"
        >
          {{ filterActive ? "All days" : "Days with Avg > 0" }}
        </button>
        <div class="flex items-center gap-3 text-xs">
          <span class="flex items-center gap-1.5 font-semibold text-blue-600">
            <span class="w-2 h-2 rounded-full bg-blue-500"></span> Cashout 1
          </span>
          <span class="flex items-center gap-1.5 font-semibold text-emerald-600">
            <span class="w-2 h-2 rounded-full bg-emerald-500"></span> Cashout 2
          </span>
        </div>
      </div>
    </div>

    <div class="overflow-x-auto" ref="scrollContainer">
      <div :style="{ minWidth: chartMinWidth }">
        <apexchart
          type="line"
          height="320"
          :options="chartOptions"
          :series="series"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ApexCharts from "vue3-apexcharts";

export default {
  name: "PlayersAvgCashoutGraph",
  components: {
    apexchart: ApexCharts,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chartMinWidth: "100%",
      filterActive: false,
    };
  },
  computed: {
    filteredDays() {
      const days = this.data.avgCashoutPerDays || [];
      if (!this.filterActive) return days;
      return days.filter(
        (d) => parseFloat(d.cashout1) !== 0 || parseFloat(d.cashout2) !== 0
      );
    },
    series() {
      return [
        {
          name: "Cashout 1",
          data: this.filteredDays.map((d) => d.cashout1),
        },
        {
          name: "Cashout 2",
          data: this.filteredDays.map((d) => d.cashout2),
        },
      ];
    },
    chartOptions() {
      return {
        chart: {
          type: "line",
          stacked: false,
          toolbar: { show: false },
          background: "transparent",
          zoom: { enabled: false },
        },
        colors: ["#3b82f6", "#10b981"],
        stroke: {
          show: true,
          width: 3,
          curve: "smooth",
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.filteredDays.map((d) => d.day.split("-")[2]),
          labels: {
            style: {
              colors: "#64748b",
              fontSize: "11px",
              fontWeight: "600",
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
          title: {
            text: "Day of Month",
            style: {
              color: "#64748b",
              fontSize: "11px",
              fontWeight: 600,
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: "#64748b",
              fontSize: "11px",
            },
            formatter: (val) => `${parseFloat(val || 0).toFixed(2)}x`,
          },
        },
        grid: {
          borderColor: "#e2e8f0",
          strokeDashArray: 3,
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: (val) => `${parseFloat(val || 0).toFixed(2)}x`,
          },
        },
        legend: {
          show: false,
        },
      };
    },
  },
  mounted() {
    this.setChartMinWidth();
  },
  watch: {
    data: {
      handler() {
        this.setChartMinWidth();
      },
      deep: true,
    },
    filterActive() {
      this.setChartMinWidth();
    },
    filteredDays() {
      this.setChartMinWidth();
    },
  },
  methods: {
    setChartMinWidth() {
      const numDays = this.filteredDays.length || 0;
      const widthPerBar = 25;
      const minW = Math.max(numDays * widthPerBar, 400);
      this.chartMinWidth = `${minW}px`;
    },
    toggleFilter() {
      this.filterActive = !this.filterActive;
    },
  },
};
</script>
